import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";

const F = "'DM Sans', system-ui, sans-serif";
const SC = { open: "#22c55e", occupied: "#ef4444", reserved: "#f59e0b" };

const NOTE_CATS = [
  { v: "scheduled-discharge", l: "Scheduled Discharge", c: "#3b82f6", bg: "#dbeafe" },
  { v: "special-circumstances", l: "Special Circumstances", c: "#f97316", bg: "#fed7aa" },
  { v: "medical-alert", l: "Medical Alert", c: "#ef4444", bg: "#fee2e2" },
  { v: "pending-intake", l: "Pending Intake", c: "#22c55e", bg: "#dcfce7" },
  { v: "maintenance", l: "Maintenance", c: "#64748b", bg: "#f1f5f9" },
  { v: "general", l: "General", c: "#a855f7", bg: "#f3e8ff" },
];

function fDate(ts) {
  if (!ts) return "";
  const d = Date.now() - new Date(ts).getTime();
  if (d < 60000) return "Just now";
  if (d < 3600000) return Math.floor(d / 60000) + "m ago";
  if (d < 86400000) return Math.floor(d / 3600000) + "h ago";
  if (d < 604800000) return Math.floor(d / 86400000) + "d ago";
  return new Date(ts).toLocaleDateString();
}

function fDT(ts) {
  return new Date(ts).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function fCat(c) { return c.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" "); }
function fM(n) { return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function Modal({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "12px", padding: "1.5rem", maxWidth: "460px", width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
        {children}
      </div>
    </div>
  );
}

// ===== MODAL FORMS =====
function AddHouseForm({ onSubmit, onClose, inp, lab, smallBtn }) {
  const [name, setName] = useState("");
  const [beds, setBeds] = useState(1);
  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontSize: "1.0625rem", fontWeight: "600", color: "#0f172a", marginBottom: "1rem", fontFamily: F }}>Add House</h3>
      <label style={lab}>House Name<input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Main House" style={{ ...inp, marginTop: "0.25rem" }} /></label>
      <label style={{ ...lab, marginTop: "0.75rem" }}>Number of Beds<input type="text" inputMode="numeric" pattern="[0-9]*" value={beds} onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ""); setBeds(v === "" ? "" : Math.max(1, parseInt(v))); }} onBlur={() => { if (!beds) setBeds(1); }} style={{ ...inp, marginTop: "0.25rem" }} /></label>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
        <button onClick={onClose} style={smallBtn("#e2e8f0", "#334155")}>Cancel</button>
        <button onClick={() => { if (name.trim()) onSubmit(name.trim(), beds); }} style={smallBtn("#0369a1")}>Add House</button>
      </div>
    </Modal>
  );
}

function AddBedForm({ houseName, nextNum, onSubmit, onClose, inp, lab, smallBtn }) {
  const [label, setLabel] = useState("Bed " + nextNum);
  const [status, setStatus] = useState("open");
  const [clientId, setClientId] = useState("");
  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontSize: "1.0625rem", fontWeight: "600", color: "#0f172a", marginBottom: "0.25rem", fontFamily: F }}>Add Bed</h3>
      <p style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, marginBottom: "1rem" }}>{houseName}</p>
      <label style={lab}>Label<input value={label} onChange={e => setLabel(e.target.value)} style={{ ...inp, marginTop: "0.25rem" }} /></label>
      <label style={{ ...lab, marginTop: "0.75rem" }}>Status
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inp, marginTop: "0.25rem" }}>
          <option value="open">Open</option>
          <option value="occupied">Occupied</option>
          <option value="reserved">Reserved</option>
        </select>
      </label>
      {status === "occupied" && (
        <label style={{ ...lab, marginTop: "0.75rem" }}>Client ID<input value={clientId} onChange={e => setClientId(e.target.value)} placeholder="Client identifier" style={{ ...inp, marginTop: "0.25rem" }} /></label>
      )}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
        <button onClick={onClose} style={smallBtn("#e2e8f0", "#334155")}>Cancel</button>
        <button onClick={() => { if (label.trim()) onSubmit(label.trim(), status, clientId); }} style={smallBtn("#0369a1")}>Add Bed</button>
      </div>
    </Modal>
  );
}

function EditBedForm({ houseName, bed, onSubmit, onDelete, onClose, inp, lab, smallBtn }) {
  const [label, setLabel] = useState(bed.label);
  const [status, setStatus] = useState(bed.status);
  const [clientId, setClientId] = useState(bed.client_id || "");
  const [notes, setNotes] = useState(bed.notes || "");
  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontSize: "1.0625rem", fontWeight: "600", color: "#0f172a", marginBottom: "0.25rem", fontFamily: F }}>Edit Bed</h3>
      <p style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, marginBottom: "1rem" }}>{houseName}</p>
      <label style={lab}>Label<input value={label} onChange={e => setLabel(e.target.value)} style={{ ...inp, marginTop: "0.25rem" }} /></label>
      <label style={{ ...lab, marginTop: "0.75rem" }}>Status
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inp, marginTop: "0.25rem" }}>
          <option value="open">Open</option>
          <option value="occupied">Occupied</option>
          <option value="reserved">Reserved</option>
        </select>
      </label>
      <label style={{ ...lab, marginTop: "0.75rem" }}>Client ID<input value={clientId} onChange={e => setClientId(e.target.value)} placeholder="Client identifier" style={{ ...inp, marginTop: "0.25rem" }} /></label>
      <label style={{ ...lab, marginTop: "0.75rem" }}>Notes<textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={{ ...inp, marginTop: "0.25rem", resize: "vertical" }} /></label>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
        <button onClick={onDelete} style={smallBtn("#fee2e2", "#991b1b")}>Delete Bed</button>
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={smallBtn("#e2e8f0", "#334155")}>Cancel</button>
        <button onClick={() => onSubmit({ label, status, clientId, notes })} style={smallBtn("#0369a1")}>Save</button>
      </div>
    </Modal>
  );
}

function AddNoteForm({ houseName, onSubmit, onClose, inp, lab, smallBtn }) {
  const [cat, setCat] = useState("general");
  const [text, setText] = useState("");
  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontSize: "1.0625rem", fontWeight: "600", color: "#0f172a", marginBottom: "0.25rem", fontFamily: F }}>Add Note</h3>
      <p style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, marginBottom: "1rem" }}>{houseName}</p>
      <label style={lab}>Category
        <select value={cat} onChange={e => setCat(e.target.value)} style={{ ...inp, marginTop: "0.25rem" }}>
          {NOTE_CATS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
      </label>
      <label style={{ ...lab, marginTop: "0.75rem" }}>Note<textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder="Enter note..." style={{ ...inp, marginTop: "0.25rem", resize: "vertical" }} /></label>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
        <button onClick={onClose} style={smallBtn("#e2e8f0", "#334155")}>Cancel</button>
        <button onClick={() => { if (text.trim()) onSubmit(cat, text.trim()); }} style={smallBtn("#0369a1")}>Add Note</button>
      </div>
    </Modal>
  );
}

function SettingsForm({ settings, onSubmit, onClose, inp, lab, smallBtn }) {
  const [rate, setRate] = useState(settings.daily_rate);
  const [days, setDays] = useState(settings.php_days);
  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontSize: "1.0625rem", fontWeight: "600", color: "#0f172a", marginBottom: "1rem", fontFamily: F }}>Bed Board Settings</h3>
      <label style={lab}>Daily Rate ($)<input type="number" step="0.01" value={rate} onChange={e => setRate(parseFloat(e.target.value) || 0)} style={{ ...inp, marginTop: "0.25rem" }} /></label>
      <label style={{ ...lab, marginTop: "0.75rem" }}>PHP Days per Week<input type="number" min="1" max="7" value={days} onChange={e => setDays(parseInt(e.target.value) || 5)} style={{ ...inp, marginTop: "0.25rem" }} /></label>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
        <button onClick={onClose} style={smallBtn("#e2e8f0", "#334155")}>Cancel</button>
        <button onClick={() => onSubmit(rate, days)} style={smallBtn("#0369a1")}>Save Settings</button>
      </div>
    </Modal>
  );
}

function ActivityLog({ log, onClose, smallBtn }) {
  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontSize: "1.0625rem", fontWeight: "600", color: "#0f172a", marginBottom: "1rem", fontFamily: F }}>Activity Log</h3>
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {log.length === 0 && <p style={{ fontSize: "0.8125rem", color: "#94a3b8", fontFamily: F }}>No activity yet.</p>}
        {log.map((entry, i) => (
          <div key={i} style={{ padding: "0.5rem 0", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: "0.8125rem", fontFamily: F }}><strong>{entry.user_name}</strong> {entry.action} {entry.table_name}</div>
            <div style={{ fontSize: "0.625rem", color: "#94a3b8", fontFamily: F }}>{fDT(entry.created_at)}</div>
          </div>
        ))}
      </div>
      <button onClick={onClose} style={{ ...smallBtn("#e2e8f0", "#334155"), marginTop: "1rem" }}>Close</button>
    </Modal>
  );
}

// ===== MAIN COMPONENT =====
export default function BedBoard() {
  const { user, api } = useAuth();
  const isDirLevel = user.role === "director" || user.role === "admin";

  const [houses, setHouses] = useState([]);
  const [actLog, setActLog] = useState([]);
  const [settings, setSettings] = useState({ daily_rate: 326.27, php_days: 5 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [housesData, settingsData, activityData] = await Promise.all([
        api.get("/bed-board/houses"),
        api.get("/bed-board/settings"),
        api.get("/bed-board/activity?limit=100"),
      ]);
      setHouses(housesData);
      setSettings(settingsData);
      setActLog(activityData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddHouse = async (name, numBeds) => {
    try {
      const newHouse = await api.post("/bed-board/houses", { name, numBeds });
      setHouses(prev => [...prev, newHouse]);
      setModal(null);
      notify("House added");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleDeleteHouse = async (houseId) => {
    if (!window.confirm("Delete this house and all its beds?")) return;
    try {
      await api.delete("/bed-board/houses/" + houseId);
      setHouses(prev => prev.filter(h => h.id !== houseId));
      notify("House deleted");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleRenameHouse = async (houseId, newName) => {
    try {
      await api.patch("/bed-board/houses/" + houseId, { name: newName });
      setHouses(prev => prev.map(h => h.id === houseId ? { ...h, name: newName } : h));
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleAddBed = async (houseId, label, status, clientId) => {
    try {
      const newBed = await api.post("/bed-board/houses/" + houseId + "/beds", { label, status, client_id: clientId });
      setHouses(prev => prev.map(h => h.id === houseId ? { ...h, beds: [...h.beds, newBed] } : h));
      setModal(null);
      notify("Bed added");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleUpdateBed = async (houseId, bedId, data) => {
    try {
      const updated = await api.patch("/bed-board/beds/" + bedId, { label: data.label, status: data.status, client_id: data.clientId, notes: data.notes });
      setHouses(prev => prev.map(h => h.id === houseId ? { ...h, beds: h.beds.map(b => b.id === bedId ? updated : b) } : h));
      setModal(null);
      notify("Bed updated");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleDeleteBed = async (houseId, bedId) => {
    if (!window.confirm("Delete this bed?")) return;
    try {
      await api.delete("/bed-board/beds/" + bedId);
      setHouses(prev => prev.map(h => h.id === houseId ? { ...h, beds: h.beds.filter(b => b.id !== bedId) } : h));
      setModal(null);
      notify("Bed deleted");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleAddNote = async (houseId, category, text) => {
    try {
      const newNote = await api.post("/bed-board/houses/" + houseId + "/notes", { category, text });
      setHouses(prev => prev.map(h => h.id === houseId ? { ...h, notes: [...h.notes, newNote] } : h));
      setModal(null);
      notify("Note added");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleDeleteNote = async (houseId, noteId) => {
    try {
      await api.delete("/bed-board/notes/" + noteId);
      setHouses(prev => prev.map(h => h.id === houseId ? { ...h, notes: h.notes.filter(n => n.id !== noteId) } : h));
      notify("Note deleted");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleUpdateSettings = async (dailyRate, phpDays) => {
    try {
      const updated = await api.patch("/bed-board/settings", { daily_rate: dailyRate, php_days: phpDays });
      setSettings(updated);
      setModal(null);
      notify("Settings saved");
    } catch (err) { notify("Error: " + err.message); }
  };

  const allBeds = houses.flatMap(h => h.beds);
  const totalBeds = allBeds.length;
  const openBeds = allBeds.filter(b => b.status === "open").length;
  const occBeds = allBeds.filter(b => b.status === "occupied").length;
  const resBeds = allBeds.filter(b => b.status === "reserved").length;
  const occRate = totalBeds > 0 ? Math.round((occBeds / totalBeds) * 100) : 0;
  const wRate = settings.daily_rate * settings.php_days;

  const inp = { width: "100%", padding: "0.5rem 0.75rem", border: "1.5px solid #d1d5db", borderRadius: "6px", fontSize: "0.875rem", fontFamily: F, outline: "none", boxSizing: "border-box" };
  const lab = { display: "block", fontSize: "0.8125rem", fontWeight: "500", color: "#374151", marginBottom: "0.25rem", fontFamily: F };
  const smallBtn = (bg, c) => ({ background: bg, color: c || "#fff", border: "none", borderRadius: "6px", padding: "0.375rem 0.625rem", fontSize: "0.6875rem", fontWeight: "600", cursor: "pointer", fontFamily: F, display: "inline-flex", alignItems: "center", gap: "0.25rem" });
  const card = { background: "#fff", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" };

  if (loading) {
    return (
      <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", fontFamily: F }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>Loading Bed Board...</div>
          <div style={{ color: "#64748b" }}>Connecting to database</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", fontFamily: F, maxWidth: "400px" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>Connection Error</div>
          <div style={{ color: "#991b1b", marginBottom: "1rem" }}>{error}</div>
          <button onClick={loadData} style={smallBtn("#0369a1")}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9" }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "1.25rem" }}>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.625rem", marginBottom: "0.875rem" }}>
          {[
            { l: "Total Beds", v: totalBeds },
            { l: "Open", v: openBeds },
            { l: "Occupied", v: occBeds },
            { l: "Reserved", v: resBeds },
            { l: "Occupancy", v: occRate + "%" },
          ].map((s, i) => (
            <div key={i} style={{ ...card, padding: "0.875rem" }}>
              <div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F, fontWeight: "500" }}>{s.l}</div>
              <div style={{ fontSize: "1.625rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{s.v}</div>
            </div>
          ))}
        </div>

        {isDirLevel && (
          <div style={{ marginBottom: "0.875rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.625rem", marginBottom: openBeds > 0 ? "0.625rem" : "0" }}>
              <div style={{ ...card, padding: "0.875rem" }}>
                <div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F }}>Weekly Earned</div>
                <div style={{ fontSize: "1.375rem", fontWeight: "700", color: "#22c55e", fontFamily: F }}>{fM(occBeds * wRate)}</div>
              </div>
              <div style={{ ...card, padding: "0.875rem" }}>
                <div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F }}>Weekly Lost</div>
                <div style={{ fontSize: "1.375rem", fontWeight: "700", color: "#ef4444", fontFamily: F }}>{fM(openBeds * wRate)}</div>
              </div>
              <div style={{ ...card, padding: "0.875rem" }}>
                <div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F }}>Full Potential</div>
                <div style={{ fontSize: "1.375rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{fM(totalBeds * wRate)}</div>
              </div>
            </div>
            {openBeds > 0 && (
              <div style={{ background: "#fef2f2", borderLeft: "4px solid #ef4444", borderRadius: "6px", padding: "0.625rem 1rem", fontSize: "0.8125rem", fontFamily: F, color: "#991b1b" }}>
                <strong>Revenue Loss:</strong> {openBeds} empty beds = {fM(openBeds * settings.daily_rate)}/day, {fM(openBeds * wRate)}/week
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: "180px" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client ID or bed..." style={inp} />
          </div>
          <button onClick={() => setModal({ type: "addHouse" })} style={smallBtn("#0369a1")}>+ House</button>
          <button onClick={() => setLogOpen(true)} style={smallBtn("#334155")}>Log ({actLog.length})</button>
          <button onClick={loadData} style={smallBtn("#e2e8f0", "#334155")}>Refresh</button>
          {isDirLevel && <button onClick={() => setModal({ type: "settings" })} style={smallBtn("#64748b")}>Settings</button>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "1rem" }}>
          {houses.map(house => {
            const hOcc = house.beds.filter(b => b.status === "occupied").length;
            const hOpen = house.beds.filter(b => b.status === "open").length;
            const hasAlert = house.notes.some(n => n.category === "medical-alert" || n.category === "special-circumstances");

            return (
              <div key={house.id} style={{ ...card, overflow: "hidden", borderLeft: hasAlert ? "4px solid #f97316" : undefined }}>
                <div style={{ background: "linear-gradient(135deg, #0f172a, #1e3a5f)", color: "#fff", padding: "0.875rem 1.125rem" }}>
                  <div style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F }}>{house.name}</div>
                  <div style={{ fontSize: "0.6875rem", opacity: 0.85, fontFamily: F }}>{house.beds.length} beds</div>
                </div>

                <div style={{ padding: "0.875rem 1.125rem" }}>
                  {isDirLevel && (
                    <div style={{ background: "#f8fafc", borderRadius: "6px", padding: "0.5rem", marginBottom: "0.625rem", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.25rem", textAlign: "center" }}>
                      <div><div style={{ fontSize: "0.5625rem", color: "#64748b", fontFamily: F }}>Earned/wk</div><div style={{ fontSize: "0.8125rem", fontWeight: "700", color: "#22c55e", fontFamily: F }}>{fM(hOcc * wRate)}</div></div>
                      <div><div style={{ fontSize: "0.5625rem", color: "#64748b", fontFamily: F }}>Lost/wk</div><div style={{ fontSize: "0.8125rem", fontWeight: "700", color: "#ef4444", fontFamily: F }}>{fM(hOpen * wRate)}</div></div>
                      <div><div style={{ fontSize: "0.5625rem", color: "#64748b", fontFamily: F }}>Potential</div><div style={{ fontSize: "0.8125rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{fM(house.beds.length * wRate)}</div></div>
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(115px, 1fr))", gap: "0.5rem", marginBottom: "0.625rem" }}>
                    {house.beds.map(bed => {
                      if (search && !bed.client_id?.toLowerCase().includes(search.toLowerCase()) && !bed.label.toLowerCase().includes(search.toLowerCase())) return null;
                      const bc = SC[bed.status] || "#cbd5e1";
                      return (
                        <div key={bed.id} onClick={() => setModal({ type: "editBed", houseId: house.id, houseName: house.name, bed })}
                          style={{ background: bc + "0a", border: "2px solid " + bc, borderRadius: "7px", padding: "0.5rem", cursor: "pointer" }}>
                          <div style={{ fontWeight: "700", fontSize: "0.875rem", fontFamily: F }}>{bed.label}</div>
                          <span style={{ display: "inline-block", background: bc, color: "#fff", fontSize: "0.5625rem", padding: "1px 5px", borderRadius: "3px", fontWeight: "600", fontFamily: F }}>{bed.status.toUpperCase()}</span>
                          {bed.client_id && <div style={{ fontSize: "0.75rem", fontWeight: "600", fontFamily: F, marginTop: "2px" }}>{bed.client_id}</div>}
                          <div style={{ fontSize: "0.5rem", color: "#64748b", fontFamily: F, marginTop: "2px" }}>{fDate(bed.last_updated)}</div>
                          {isDirLevel && bed.status === "open" && (
                            <div style={{ background: "#ef4444", color: "#fff", fontSize: "0.5rem", padding: "1px 4px", borderRadius: "2px", fontWeight: "600", marginTop: "4px", display: "inline-block", fontFamily: F }}>-{fM(settings.daily_rate)}/day</div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {house.notes.length > 0 && (
                    <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "0.5rem" }}>
                      <div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F, marginBottom: "0.375rem" }}>Notes ({house.notes.length})</div>
                      {house.notes.map(note => {
                        const ct = NOTE_CATS.find(c => c.v === note.category);
                        return (
                          <div key={note.id} style={{ background: "#f8fafc", borderLeft: "3px solid " + (ct ? ct.c : "#cbd5e1"), borderRadius: "4px", padding: "0.375rem 0.625rem", marginBottom: "0.25rem" }}>
                            <span style={{ display: "inline-block", background: ct ? ct.bg : "#f1f5f9", color: ct ? ct.c : "#64748b", fontSize: "0.5rem", padding: "1px 4px", borderRadius: "2px", fontWeight: "600", fontFamily: F }}>{fCat(note.category)}</span>
                            <div style={{ fontSize: "0.75rem", fontFamily: F, margin: "2px 0" }}>{note.text}</div>
                            <div style={{ fontSize: "0.5rem", color: "#94a3b8", fontFamily: F }}>
                              {fDT(note.created_at)}
                              <span onClick={() => handleDeleteNote(house.id, note.id)} style={{ color: "#ef4444", cursor: "pointer", marginLeft: "8px", textDecoration: "underline" }}>Delete</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "0.25rem", marginTop: "0.625rem", flexWrap: "wrap" }}>
                    <button onClick={() => setModal({ type: "addBed", houseId: house.id, houseName: house.name, nextNum: house.beds.length + 1 })} style={smallBtn("#0369a1")}>+ Bed</button>
                    <button onClick={() => setModal({ type: "addNote", houseId: house.id, houseName: house.name })} style={smallBtn("#e2e8f0", "#334155")}>+ Note</button>
                    <button onClick={() => { const n = window.prompt("Rename house:", house.name); if (n) handleRenameHouse(house.id, n); }} style={smallBtn("#e2e8f0", "#334155")}>Edit</button>
                    <button onClick={() => handleDeleteHouse(house.id)} style={smallBtn("#fee2e2", "#991b1b")}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      {modal && modal.type === "addHouse" && (
        <AddHouseForm onSubmit={handleAddHouse} onClose={() => setModal(null)} inp={inp} lab={lab} smallBtn={smallBtn} />
      )}
      {modal && modal.type === "addBed" && (
        <AddBedForm houseName={modal.houseName} nextNum={modal.nextNum} onSubmit={(l, s, c) => handleAddBed(modal.houseId, l, s, c)} onClose={() => setModal(null)} inp={inp} lab={lab} smallBtn={smallBtn} />
      )}
      {modal && modal.type === "editBed" && (
        <EditBedForm houseName={modal.houseName} bed={modal.bed} onSubmit={(data) => handleUpdateBed(modal.houseId, modal.bed.id, data)} onDelete={() => handleDeleteBed(modal.houseId, modal.bed.id)} onClose={() => setModal(null)} inp={inp} lab={lab} smallBtn={smallBtn} />
      )}
      {modal && modal.type === "addNote" && (
        <AddNoteForm houseName={modal.houseName} onSubmit={(cat, text) => handleAddNote(modal.houseId, cat, text)} onClose={() => setModal(null)} inp={inp} lab={lab} smallBtn={smallBtn} />
      )}
      {modal && modal.type === "settings" && (
        <SettingsForm settings={settings} onSubmit={handleUpdateSettings} onClose={() => setModal(null)} inp={inp} lab={lab} smallBtn={smallBtn} />
      )}
      {logOpen && (
        <ActivityLog log={actLog} onClose={() => setLogOpen(false)} smallBtn={smallBtn} />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: "1.25rem", right: "1.25rem", background: "#22c55e", color: "#fff", padding: "0.625rem 1rem", borderRadius: "8px", boxShadow: "0 8px 20px rgba(0,0,0,0.2)", zIndex: 400, fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>
          {toast}
        </div>
      )}
    </div>
  );
}
